/**
 * XRConfigTab — v10.8.0
 * Pestaña "XR / Realidad Aumentada" del ScenarioEditor. Solo visible cuando
 * el flag `xr_enabled` está activo.
 *
 * Permite:
 *  - Activar XR para este escenario
 *  - Elegir modos soportados (mobile_ar, tablet_ar, mixed_reality, ar_glasses)
 *  - Seleccionar marcadores/zonas/procedimientos habilitados
 *  - Configurar interacciones básicas
 */
import { useEffect, useState } from 'react';
import api from '../../api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Info, Cube, DeviceMobile, Path, MapPin } from '@phosphor-icons/react';

const XR_MODES = [
  { value: 'mobile_ar', label: 'Móvil AR', description: 'ARKit/ARCore vía teléfono' },
  { value: 'tablet_ar', label: 'Tablet AR', description: 'iPad/Android tablet con cámara' },
  { value: 'mixed_reality', label: 'Realidad Mixta', description: 'Meta Quest 3 (passthrough + hand tracking)' },
  { value: 'ar_glasses', label: 'Gafas AR', description: 'XREAL / Rokid con tracking 6DoF' },
];

export default function XRConfigTab({ config, onChange }) {
  const cfg = config || {};
  const modes = cfg.modes || [];
  const enabled = !!cfg.enabled;

  const [markers, setMarkers] = useState([]);
  const [zones, setZones] = useState([]);
  const [procedures, setProcedures] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get('/xr/markers').then((r) => r.data?.markers || []).catch(() => []),
      api.get('/xr/zones').then((r) => r.data?.zones || []).catch(() => []),
      api.get('/xr/procedures').then((r) => r.data?.procedures || []).catch(() => []),
    ]).then(([m, z, p]) => { setMarkers(m); setZones(z); setProcedures(p); });
  }, []);

  const setCfg = (patch) => onChange({ ...cfg, ...patch });

  const toggleMode = (mode) => {
    const set = new Set(modes);
    if (set.has(mode)) set.delete(mode); else set.add(mode);
    setCfg({ modes: Array.from(set) });
  };

  const toggleMarker = (id) => {
    const set = new Set(cfg.enabled_markers || []);
    if (set.has(id)) set.delete(id); else set.add(id);
    setCfg({ enabled_markers: Array.from(set) });
  };
  const toggleZone = (code) => {
    const set = new Set(cfg.enabled_zones || []);
    if (set.has(code)) set.delete(code); else set.add(code);
    setCfg({ enabled_zones: Array.from(set) });
  };
  const toggleProcedure = (code) => {
    const set = new Set(cfg.enabled_procedures || []);
    if (set.has(code)) set.delete(code); else set.add(code);
    setCfg({ enabled_procedures: Array.from(set) });
  };

  return (
    <div className="space-y-4" data-testid="xr-config-tab">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2"><Cube size={20} className="text-cyan-600" /> Configuración XR</CardTitle>
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-600">Habilitar XR en este escenario</span>
              <Switch checked={enabled} onCheckedChange={(v) => setCfg({ enabled: v })} data-testid="xr-enabled-switch" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-2 p-3 rounded-md bg-cyan-50 border border-cyan-200 text-sm text-cyan-900">
            <Info size={16} className="mt-0.5 shrink-0" />
            <span>Al activarlo, el escenario aparecerá disponible para dispositivos XR vinculados. Los eventos XR utilizan el mismo motor fisiológico y de supervisión que la interfaz web.</span>
          </div>
        </CardContent>
      </Card>

      {enabled && (
        <>
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><DeviceMobile size={18} /> Modos soportados</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {XR_MODES.map((m) => (
                  <button
                    key={m.value}
                    onClick={() => toggleMode(m.value)}
                    data-testid={`xr-mode-${m.value}`}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${modes.includes(m.value) ? 'border-cyan-500 bg-cyan-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-sm">{m.label}</span>
                      {modes.includes(m.value) && <Badge className="bg-cyan-100 text-cyan-700 text-[10px]">Activo</Badge>}
                    </div>
                    <span className="text-xs text-slate-500">{m.description}</span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><MapPin size={18} /> Marcadores habilitados</CardTitle></CardHeader>
            <CardContent>
              {markers.length === 0 ? (
                <div className="text-xs text-slate-400 text-center py-3">No hay marcadores creados. Ve a Mantenedores → SimuClinic XR.</div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {markers.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => toggleMarker(m.marker_id)}
                      className={`p-2 rounded border text-left transition-all ${(cfg.enabled_markers || []).includes(m.marker_id) ? 'border-cyan-500 bg-cyan-50' : 'border-slate-200'}`}
                    >
                      <div className="text-xs font-mono text-cyan-700">{m.marker_id}</div>
                      <div className="text-xs text-slate-600">{m.label || m.marker_type}</div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Zonas anatómicas habilitadas</CardTitle></CardHeader>
            <CardContent>
              {zones.length === 0 ? (
                <div className="text-xs text-slate-400 text-center py-3">No hay zonas creadas.</div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {zones.map((z) => (
                    <button
                      key={z.code}
                      onClick={() => toggleZone(z.code)}
                      className={`p-2 rounded border text-left transition-all ${(cfg.enabled_zones || []).includes(z.code) ? 'border-purple-500 bg-purple-50' : 'border-slate-200'}`}
                    >
                      <div className="text-xs font-mono text-purple-700">{z.code}</div>
                      <div className="text-xs">{z.label}</div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Path size={18} /> Procedimientos guiados</CardTitle></CardHeader>
            <CardContent>
              {procedures.length === 0 ? (
                <div className="text-xs text-slate-400 text-center py-3">No hay procedimientos creados.</div>
              ) : (
                <div className="space-y-1">
                  {procedures.map((p) => (
                    <button
                      key={p.code}
                      onClick={() => toggleProcedure(p.code)}
                      className={`w-full flex items-center gap-2 p-2 rounded border text-left transition-all ${(cfg.enabled_procedures || []).includes(p.code) ? 'border-amber-500 bg-amber-50' : 'border-slate-200'}`}
                    >
                      <span className="text-xs font-mono text-amber-700">{p.code}</span>
                      <span className="text-sm flex-1">{p.name}</span>
                      <span className="text-[10px] text-slate-400">{(p.steps || []).length} pasos</span>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Instrucciones adicionales</CardTitle></CardHeader>
            <CardContent>
              <Label className="text-xs">Notas para el alumno (opcional)</Label>
              <Textarea rows={3} value={cfg.instructions || ''} onChange={(e) => setCfg({ instructions: e.target.value })} placeholder="Al iniciar, apunta la cámara al maniquí. Cada marcador representa una zona anatómica..." />
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
